import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RichTextEditor } from '@/shared/components/custom/rich-text-editor';

interface TaskDescriptionEditorProps {
  value: string;
  onChange: (value: string) => void;
  saving?: boolean;
}

const DEBOUNCE_MS = 1500;

export function TaskDescriptionEditor({ value, onChange, saving }: TaskDescriptionEditorProps) {
  const { t } = useTranslation('tasks');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleChange = (json: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChangeRef.current(json), DEBOUNCE_MS);
  };

  return (
    <div data-testid="tasks-description-editor" className="relative">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{t('description.label')}</p>
      <RichTextEditor
        value={value}
        onChange={handleChange}
        placeholder={t('description.placeholder')}
        variant="plain"
        enableSlashCommands
        enableDragHandle
        className="min-h-32"
      />
      {saving && (
        <span className="absolute bottom-2 right-2 text-xs text-muted-foreground">
          {t('description.saving')}
        </span>
      )}
    </div>
  );
}
